import { PlaceholderCard } from '@components/admin/PlaceholderCard';
import { LifecycleFields } from './LifecycleFields';

interface ReservasSectionProps {
  confirmationMode: 'auto' | 'manual';
  setConfirmationMode: (v: 'auto' | 'manual') => void;
  checkInEnabled: boolean;
  setCheckInEnabled: (v: boolean) => void;
  slotStepMinutes: number;
  setSlotStepMinutes: (v: number) => void;
  pendingTtlHours: number;
  setPendingTtlHours: (v: number) => void;
  approvalTtlHours: number;
  setApprovalTtlHours: (v: number) => void;
  checkinCompletionBufferMinutes: number;
  setCheckinCompletionBufferMinutes: (v: number) => void;
  roomsAffectAvailability: boolean;
  setRoomsAffectAvailability: (v: boolean) => void;
  showRoomToCustomer: boolean;
  setShowRoomToCustomer: (v: boolean) => void;
  useSchedulesForAvailability: boolean;
  setUseSchedulesForAvailability: (v: boolean) => void;
}

export function ReservasSection(p: ReservasSectionProps) {
  return (
    <>
      {/* Política (placeholder — antelación, cancelación, lista espera) */}
      <PlaceholderCard
        title="Política de reservas"
        description="Reglas que se aplican a todas las reservas entrantes."
        preview={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="field-row">
              <div className="field">
                <label className="field-label">Antelación mínima</label>
                <select className="select" disabled><option>2 horas</option></select>
              </div>
              <div className="field">
                <label className="field-label">Antelación máxima</label>
                <select className="select" disabled><option>60 días</option></select>
              </div>
            </div>
            <div className="field-row">
              <div className="field">
                <label className="field-label">Cancelación gratuita hasta</label>
                <select className="select" disabled><option>24 horas antes</option></select>
              </div>
              <div className="field">
                <label className="field-label">Penalización tardía</label>
                <select className="select" disabled><option>50% del servicio</option></select>
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" disabled />
              Permitir lista de espera
            </label>
          </div>
        }
      />

      {/* Confirmación (real) */}
      <div className="card">
        <div className="card-head"><span className="card-title">Modo de confirmación de reservas</span></div>
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: 0 }}>
            Controla si las reservas se confirman automáticamente o requieren aprobación del centro.
          </p>
          {([
            ['auto', 'Automático', 'El cliente confirma por email y la reserva queda directamente confirmada.'],
            ['manual', 'Aprobación manual', 'Tras confirmar el cliente, la reserva queda en "Pend. aprobación" hasta que un admin la apruebe o rechace.'],
          ] as const).map(([mode, title, desc]) => (
            <label key={mode} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="radio" name="confirmationMode" value={mode} checked={p.confirmationMode === mode} onChange={() => { p.setConfirmationMode(mode); }} style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 500, fontSize: 13.5 }}>{title}</div>
                <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Check-in (real) */}
      <div className="card">
        <div className="card-head"><span className="card-title">Check-in</span></div>
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={p.checkInEnabled}
              onChange={(e) => { p.setCheckInEnabled(e.target.checked); }}
              style={{ width: 14, height: 14, accentColor: 'var(--accent)' }}
            />
            <span style={{ fontWeight: 500, fontSize: 13.5 }}>Activar paso de check-in</span>
          </label>
          <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', margin: 0 }}>
            Si está activado, las reservas confirmadas deben pasar por "En sala" antes de poder marcarse como completadas. Útil para centros con recepción.
          </p>
        </div>
      </div>

      {/* Granularidad de slots (real) */}
      <div className="card">
        <div className="card-head"><span className="card-title">Granularidad de reservas</span></div>
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: 0 }}>
            Cada cuántos minutos se ofrecen huecos de inicio al cliente. Es independiente de
            la duración del servicio: un servicio de 30 min con step de 15 ofrece inicios cada
            15 minutos (09:00, 09:15, 09:30, …).
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13.5, fontWeight: 500 }}>Step</span>
            <select
              className="select"
              value={p.slotStepMinutes}
              onChange={(e) => { p.setSlotStepMinutes(parseInt(e.target.value, 10)); }}
              style={{ width: 130 }}
            >
              <option value={5}>5 min</option>
              <option value={10}>10 min</option>
              <option value={15}>15 min</option>
              <option value={30}>30 min</option>
              <option value={60}>60 min</option>
            </select>
          </label>
        </div>
      </div>

      {/* Ciclo de vida (real) */}
      <div className="card">
        <div className="card-head"><span className="card-title">Ciclo de vida automático de reservas</span></div>
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: 0 }}>
            El sistema cancela automáticamente las reservas no confirmadas a tiempo y cierra las citas que el staff olvida marcar como completadas.
          </p>
          <LifecycleFields
            pendingTtlHours={p.pendingTtlHours}
            setPendingTtlHours={p.setPendingTtlHours}
            approvalTtlHours={p.approvalTtlHours}
            setApprovalTtlHours={p.setApprovalTtlHours}
            checkinCompletionBufferMinutes={p.checkinCompletionBufferMinutes}
            setCheckinCompletionBufferMinutes={p.setCheckinCompletionBufferMinutes}
          />
        </div>
      </div>

      {/* Salas (real, 3 flags) */}
      <div className="card">
        <div className="card-head"><span className="card-title">Salas</span></div>
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: 0 }}>
            Define si las salas afectan a la disponibilidad de reservas. La asignación
            servicio→salas se gestiona desde la página de Servicios (opt-in: con salas
            marcadas se restringe; sin marcas, cualquier sala libre del centro vale).
          </p>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={p.roomsAffectAvailability}
              onChange={(e) => { p.setRoomsAffectAvailability(e.target.checked); }}
              style={{ width: 14, height: 14, accentColor: 'var(--accent)', marginTop: 2 }}
            />
            <div>
              <div style={{ fontWeight: 500, fontSize: 13.5 }}>Las salas restringen la disponibilidad</div>
              <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                Si está desactivado, las salas son sólo etiqueta. Si está activado, una reserva sólo
                prospera cuando hay staff <em>y</em> al menos una sala libres.
              </div>
            </div>
          </label>
          {p.roomsAffectAvailability && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginLeft: 24 }}>
              <input
                type="checkbox"
                checked={p.showRoomToCustomer}
                onChange={(e) => { p.setShowRoomToCustomer(e.target.checked); }}
                style={{ width: 14, height: 14, accentColor: 'var(--accent)', marginTop: 2 }}
              />
              <div>
                <div style={{ fontWeight: 500, fontSize: 13.5 }}>Mostrar la sala al cliente</div>
                <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                  Si está activado, el email de confirmación y el .ics incluyen el nombre de la sala asignada.
                </div>
              </div>
            </label>
          )}
        </div>
      </div>

      {/* Horarios staff (real) */}
      <div className="card">
        <div className="card-head"><span className="card-title">Horarios de profesionales</span></div>
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: 0 }}>
            Por defecto los horarios asignados en <em>Horarios</em> son solo planning interno y no
            afectan al widget. Activa este toggle para que los slots fuera del rango asignado
            dejen de aparecer en las reservas online.
          </p>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={p.useSchedulesForAvailability}
              onChange={(e) => { p.setUseSchedulesForAvailability(e.target.checked); }}
              style={{ width: 14, height: 14, accentColor: 'var(--accent)', marginTop: 2 }}
            />
            <div>
              <div style={{ fontWeight: 500, fontSize: 13.5 }}>Usar horarios para disponibilidad</div>
              <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                Si está activo, los slots fuera del rango asignado en <em>Horarios</em> no aparecen
                en el widget. Si un profesional no tiene horario para esa semana, se comporta como
                hasta ahora (disponible según el horario del centro). Las ausencias (vacaciones / baja)
                siempre se aplican.
              </div>
            </div>
          </label>
        </div>
      </div>
    </>
  );
}
